<script setup>
import notification from "@/utils/plugins/NotificationService";
// Componentes
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
// Services
import courseService from "@/api/services/course.service";
import courseSessionsService from "@/api/services/courseSession.service";
import EmployeeService from '@/api/services/employee.service';

import { computed, onMounted, ref } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import { useRouter } from "vue-router";



const authStore = useAuthStore();
const uiStore = useUiStore();


const modalitySearch = ref();
const modalityOptions = ref([
    { name: "Online", value: "online" },
    { name: "Presencial", value: "presential" },
    { name: "Hibrido", value: "hybrid" },
]);

const enrollCourseSessionDialog = ref(false)
const nameCourse = ref();
const employees = ref([])

const totalResults = ref();
const filteredCourses = ref();

const selectedCourseSession = ref(null)
const selectedCourse = ref(null)
const params = ref({})
const page = ref(1);
const itemsPerPage = ref(30)


const router = useRouter();

const user = computed(() => {
    return authStore.getUserLogged;
})
const isAdmin = computed(() => {
    if (user.value) {
        return user.value.roles.includes('ROLE_ADMIN');
    }
    return false;
})

const isEnrolled = computed(() => {
    const session = selectedCourseSession.value;

    if (!session || !Array.isArray(session.enrollments)) {
        return false;
    }

    return session.enrollments.some(enroll => {
        const emp = enroll.employee;
        if (emp && emp['@id']) {
            return emp['@id'] === user.value['@id']
        }
        return false;
    })
})

onMounted(async () => {
    params.value = {
        page: page.value,
        itemsPerPage: itemsPerPage.value,
        'order[assignedAt]': "ASC"
    }
    await startData()
})

const startData = async () => {
    if (isAdmin.value) {
        await Promise.all([getCourses(), getEmployees()]);
    } else {
        await getCourseSessions();
    }
}

const searchCourses = async () => {
    params.value = {
        page: page.value,
        itemsPerPage: itemsPerPage.value,
        'order[assignedAt]': "ASC"
    }

    if (isAdmin.value) {
        params.value.name = nameCourse.value;
        params.value.modality = modalitySearch.value;
        await getCourses();
    } else {
        params.value['course.name'] = nameCourse.value;
        await getCourseSessions();
    }

}

const getCourses = async () => {

    uiStore.startLoading();
    try {
        const res = await courseService.getCourses(params.value);
        if (res.status === 200) {
            totalResults.value = res.data['hydra:totalItems']
            filteredCourses.value = res.data['hydra:member']
        }
        params.value.name = "";
    } finally {
        uiStore.stopLoading();
    }
}
const getCourseSessions = async () => {
    uiStore.startLoading();
    try {
        const res = await courseSessionsService.getCoursesSession(params.value);
        if (res.status === 200) {
            totalResults.value = res.data['hydra:totalItems']
            filteredCourses.value = res.data['hydra:member']
        }
        params.value.name = "";
    } finally {
        uiStore.stopLoading();
    }
}

const getEmployees = async () => {
    const par = {
        'order[name]': 'asc',
        'active': 1,
        page: 1,
        itemsPerPage: 999999,
    };
    uiStore.startLoading();
    try {
        const res = await EmployeeService.getEmployees(par);
        employees.value = res.data['hydra:member'];
    } finally {
        uiStore.stopLoading();
    }
}

const newCourse = () => {
    router.push("/training/newCourse");
}

const editCourse = (course) => {

    const id = course.id;
    router.push("/training/course/" + id);
}

const openCourseMobile = (course) => {
    if (isAdmin.value && window.matchMedia('(max-width: 767px)').matches) {
        editCourse(course);
    }
}

const openDialogEnrollCourseSession = (courseSession) => {
    if (courseSession && courseSession.course) {
        selectedCourse.value = courseSession.course;
    }
    selectedCourseSession.value = courseSession;
    enrollCourseSessionDialog.value = true;
}
const closeDialogEnrollCourseSession = () => {
    enrollCourseSessionDialog.value = false;
    selectedCourseSession.value = null;
}

const openDialogEnrollCourseSessionAdmin = (course) => {
    const session = getCurrentCourseSession(course);
    selectedCourse.value = course;
    openDialogEnrollCourseSession(session);
}

const getCurrentCourseSession = (course) => {
    const now = new Date();

    return course.courseSessions?.findLast(session =>
        session.status === "ongoing" &&
        (!session.endAt || new Date(session.endAt) > now)
    );
}

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // formato 24 h
    };

    return date.toLocaleString('es-ES', options).replace(',', '');
}

const enrollToCourseSession = async () => {
    if (!selectedCourseSession.value) {
        notification.addNotification({
            message: 'Ha habido un error al inscribirte',
            type: 'error'
        });
        return
    }
    uiStore.startLoading();
    try {
        const res = await courseSessionsService.enrollToCourseSession(selectedCourseSession.value.id, params.value);
        if (res.status === 200 || res.status === 201) {
            notification.addNotification({
                message: 'Te has inscrito a ese curso',
                type: 'success'
            });
        }
        closeDialogEnrollCourseSession();
        await startData();
    } catch {
        notification.addNotification({
            message: 'Ha habido un problema al inscribirte',
            type: 'error'
        });
    } finally {
        uiStore.stopLoading();
    }

}

const isAvailableCourseSession = (course) => {
    const now = new Date();
    if (course.availableSeats > 0) {
        if (course.status === "ongoing") {
            if (course.endAt && new Date(course.endAt) > now) {
                return true;
            }
            if (!course.endAt) return true;
        }
    }
    return false;
}


</script>

<template>
    <div>
        <header-title title="Talento" />
        <div class="d-flex row">
            <div class="d-flex flex-row justify-content-start el-col-xs-24 col-md-6">
                <!-- TODO: Cambiar svg -->
                <img style="width: 35px;" src="@/assets/icons/graduating-hat-blue.svg">
                <div class="title-text pl-4">
                    Formación
                </div>
            </div>
        </div>
        <!-- Vista Cursos Admin -->
        <div v-if="isAdmin">
            <!-- Barra de busqueda -->
            <el-row class="mt-2">
                <el-card class="card-search card-abs w-100 wback" body-style="padding:0">
                    <div class="search-form">
                        <div class="row">
                            <div class="col-md-4 el-col-xs-24">
                                <div class="form-item">
                                    <label>Nombre del curso</label>
                                    <el-input v-model="nameCourse" placeholder="Buscar por nombre" clearable
                                        @change="searchCourses()">
                                    </el-input>
                                </div>
                            </div>
                            <div class="col-md-4 el-col-xs-24">
                                <div class="form-item">
                                    <label class="w-100">Modalidad</label>
                                    <el-select v-model="modalitySearch" placeholder="Todos" filterable clearable
                                        @change="searchCourses()">
                                        <el-option v-for="mod in modalityOptions" :key="mod.value" :label="mod.name"
                                            :value="mod.value">
                                        </el-option>
                                    </el-select>
                                </div>
                            </div>
                            <div class="col-md-4 el-col-xs-24">
                                <div class="d-flex justify-content-center course-search-buttons">
                                    <div class="col-md-6 align-self-center el-col-xs-24">
                                        <button type="submit" class="btn btn_red btn__new" @click="newCourse()"
                                            v-if="isAdmin">
                                            CREAR
                                        </button>
                                    </div>
                                    <div class="col-md-6 align-self-center el-col-xs-24">
                                        <button type="submit" @click="searchCourses()"
                                            class="btn btn_yellow btn__search">
                                            BUSCAR
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </el-card>
            </el-row>
            <!-- Tabla cursos-->
            <el-row v-for="course in filteredCourses" :key="course.id">
                <div class="card card-course w-100 pt-2 px-2 mt-2 justify-content-center"
                    @click="openCourseMobile(course)">
                    <div class="d-flex  align-items-center justify-content-center card-course-item"
                        style="margin-top: -7px;">
                        <div class="d-flex align-items-center col-md-5 el-col-xs-24">
                            <img style="width: 30px; margin-right: 7px;" src="@/assets/icons/graduating-hat-blue.svg">
                            <span class="title-course">{{ course.name }}</span>
                        </div>
                        <div class="d-flex align-items-center justify-content-end col-1 course-item-divider"
                            style="margin-right: 10px;">
                            <el-divider class="col-auto" direction="vertical"
                                style="margin: 0; height: 45px;"></el-divider>
                            <!-- <b class="time-course">Hoy 11:19</b>
                                <el-divider class="col-auto" direction="vertical"
                                    style="margin: 0; height: 45px;"></el-divider> -->
                        </div>
                        <div class="d-flex col-md-5 el-col-xs-24 course-item-description">
                            {{ course.description }}
                        </div>
                        <div
                            class="d-flex align-items-center justify-content-end col-md-1 el-col-xs-24 course-item-see-button">
                            <div class="pointer course-open-action" style="margin-right: 6px;" v-if="isAdmin">
                                <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Editar"
                                    @click="editCourse(course)">
                                    <font-awesome-icon icon="circle" style="color: #f7d20e;" />
                                    <font-awesome-icon icon="eye" class="fa-inverse" transform="shrink-6"
                                        style=" margin-left: -1.45px;" />
                                </font-awesome-layers>
                            </div>
                            <div class="pointer" style="margin-right: 6px;" v-if="getCurrentCourseSession(course)">
                                <font-awesome-layers class="fa-2x" title="Inscribirse"
                                    @click.stop="openDialogEnrollCourseSessionAdmin(course)">
                                    <font-awesome-icon icon="circle" style="color: green" />
                                    <font-awesome-icon icon="check" class="fa-inverse" transform="shrink-5" />
                                </font-awesome-layers>
                            </div>
                        </div>
                    </div>
                </div>
            </el-row>
        </div>
        <!-- Vista Cursos Usuario -->
        <div v-else>
            <!-- Barra de busqueda -->
            <el-row class="mt-2">
                <el-card class="card-search card-abs w-100 wback" body-style="padding:0">

                    <div class="search-form">
                        <div class="row">
                            <div class="col-md-6 col-xs-12">
                                <div class="form-item">
                                    <label>Nombre del curso</label>
                                    <el-input v-model="nameCourse" placeholder="Buscar por nombre" clearable
                                        @change="searchCourses()">
                                    </el-input>
                                </div>
                            </div>
                            <div class="col-2">
                                <!-- <div class="form-item">
                                        <label class="w-100">Departamento</label>
                                        <el-select v-model="department" placeholder="Todos" filterable clearable
                                            @change="search()">
                                            <el-option v-for="item in departments" :key="item.id" :label="item.name"
                                                :value="item.name">
                                            </el-option>
                                        </el-select>
                                    </div> -->
                            </div>
                            <div class="col-md-4 col-xs-12">
                                <div class="d-flex justify-content-center course-search-buttons">
                                    <div class="col-md-6 align-self-center el-col-xs-24">
                                        <button type="submit" class="btn btn_red btn__new" @click="newCourse()"
                                            v-if="isAdmin">
                                            CREAR
                                        </button>
                                    </div>
                                    <div class="col-md-6 align-self-center el-col-xs-24">
                                        <button type="submit" @click="searchCourses()"
                                            class="btn btn_yellow btn__search">
                                            BUSCAR
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </el-card>
            </el-row>
            <!-- Tabla cursos-->
            <el-row v-for="course in filteredCourses" :key="course.id">
                <div class="card card-course w-100 pt-2 px-2 mt-2 justify-content-center"
                    v-if="isAvailableCourseSession(course)">
                    <div class="d-flex align-items-center justify-content-center card-course-item"
                        style="margin-top: -7px;">
                        <div class="d-flex align-items-center col-md-7 el-col-xs-24">
                            <img style="width: 30px; margin-right: 7px;" src="@/assets/icons/graduating-hat-blue.svg">
                            <span class="title-course">{{ course && course.course ?
                                course.course.name :
                                'Nombre del curso' }}</span>
                        </div>
                        <div class="d-flex align-items-center justify-content-end col-md-4 el-col-xs-24 card-course-session-hour"
                            style="margin-right: 4px;">
                            <el-divider class="col-auto course-item-divider" direction="vertical"
                                style="margin: 0; height: 45px;"></el-divider>
                            <b class="time-course">{{ formatDate(course.startAt) }}</b>
                            <el-divider class="col-auto justify-content-center" direction="vertical"
                                style="margin: 0; height: 45px;"></el-divider>
                            <b class="time-course">{{ course.endAt ? formatDate(course.endAt) :
                                'Duración indefinida' }}</b>
                            <el-divider class="col-auto course-item-divider" direction="vertical"
                                style="margin: 0; height: 45px;"></el-divider>
                        </div>
                        <div class="d-flex align-items-center date-presence justify-content-end col-md-1 el-col-xs-24">
                            <div class="pointer" style="margin-right: 6px;">
                                <font-awesome-layers class="fa-2x" title="Inscribirse"
                                    @click.stop="openDialogEnrollCourseSession(course)">
                                    <font-awesome-icon icon="circle" style="color: green" />
                                    <font-awesome-icon icon="check" class="fa-inverse" transform="shrink-5" />
                                </font-awesome-layers>
                            </div>
                        </div>
                    </div>
                </div>
            </el-row>
        </div>
    </div>
    <!-- Dialog Inscribirte a curso -->
    <el-dialog v-model="enrollCourseSessionDialog" width="500" :show-close="false" :lock-scroll="true"
        style="border-radius: 20px;" @close="closeDialogEnrollCourseSession">
        <div v-if="selectedCourseSession">
            <h3>Deseas Inscribirte a este curso:</h3>
            <div class="f-flex">
                <div class="col-md-12 el-col-xs-24">
                    <span class="title-course">{{ selectedCourse.name }}</span>
                </div>
            </div>
            <div class="row d-flex">
                <div class="el-col-xs-24 col-md-12" v-if="selectedCourse && selectedCourse.description">
                    <label class="w-100" for="description">Descripci&oacute;n</label>
                    <textarea v-model="selectedCourse.description" id="description" class="w-100 p-3 font18" disabled />
                </div>
                <div class="el-col-xs-24 col-md-6" v-if="selectedCourseSession.startAt">
                    <div class="form-item date-picker">
                        <label class="mb-2 w-100" for="dateStart">Fecha inicio</label>
                        <el-date-picker class="w-100" v-model="selectedCourseSession.startAt" type="date" disabled
                            id="dateStart" />
                    </div>
                </div>
                <div class="el-col-xs-24 col-md-6" v-if="selectedCourseSession.endAt">
                    <div class="form-item date-picker">
                        <label class="mb-2 w-100" for="dateEnd">Fecha fin</label>
                        <el-date-picker class="w-100" v-model="selectedCourseSession.endAt" type="date" disabled
                            id="dateEnd" />
                    </div>
                </div>
                <div class="el-col-xs-24 col-md-6" v-if="selectedCourseSession.availableSeats">
                    <div class="form-item date-picker">
                        <label for="availableSeats" class="">Asientos disponibles</label>
                        <el-input-number id="availableSeats" v-model="selectedCourseSession.availableSeats"
                            class="search-input" style="width: 100%;" disabled :controls="false" />
                    </div>
                </div>
                <div class="el-col-xs-24 col-md-6" v-if="selectedCourseSession.location">
                    <div class="form-item">
                        <label for="location">Localización</label>
                        <el-input id="location" v-model="selectedCourseSession.location" disabled />
                    </div>
                </div>
            </div>
            <div class="row item justify-content-end" style="margin-right: 5px;">
                <div class="col-sm-5" v-if="isEnrolled">
                    <button class="btn btn_red" @click="closeDialogEnrollCourseSession">
                        Cancelar
                    </button>
                </div>
                <div class="col-sm-5" v-if="!isEnrolled">
                    <button class="btn btn_green" @click="enrollToCourseSession();">
                        Inscribirme
                    </button>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<style lang="scss" scoped>
.filter-options {
    font-weight: bold;
    font-size: 32px;
}

.option {
    cursor: pointer;
    color: #3B47FF;
    /* Azul por defecto */
    margin: 0 0.2em;
}

.option.active {
    color: #FF006E;
    /* Rosa cuando está activo */
}

.separator {
    color: #3B47FF;
    /* Separador siempre azul */
    margin: 0 0.2em;
}

.btn__search,
.btn__new {
    height: 3em;
    margin-top: 22px;
}

.card-course {
    min-height: 71px;
    border-radius: 20px;
}

.title-course {
    font-weight: 800;
    font-size: 18px;
    color: #3B47FF;
}

.time-course {
    font-weight: 600;
    font-size: 13px;
    color: #3B47FF;
    margin-right: 7px;
    margin-left: 7px;
    text-align: center;
}

.employee-text {
    align-items: center;
    align-content: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

@media (max-width: 767px) {
    .card-course {
        cursor: pointer;
        position: relative;
    }

    .course-open-action {
        display: none;
    }

    .course-item-see-button,
    .date-presence {
        position: absolute;
        right: 12px;
        top: 10px;
        width: auto !important;
        z-index: 1;
    }

    .card-course-item {
        padding-right: 42px;
    }
}
</style>
